// ─── AboveInputZone — unified "Contribution Points" slot above the composer ───────
//
// docs/v2/spec/PLUGIN_MODULES.md (Contribution Points). ONE place collects every
// above-input contribution so they never float ad-hoc or shove the composer around:
//   • chips  — small pills (restore-prompt, desync hint, future plugin chips) in a
//              horizontal row. When the row OVERFLOWS its width the extras collapse into
//              a hamburger menu, measured live with ResizeObserver (true width, not a
//              count guess) so it stays correct across resize / mobile / new plugins.
//   • cards  — large blocks (the pinned prompt / menu widget) stack full-width below.
//
// Shared by WhimerTerm² (WhimerNativeView) and WhimerClean² (WhimerCleanView).

import { useState, useRef, useLayoutEffect, type ReactNode } from 'react'
import { Icon } from '../Icon'

export interface ZoneChip {
  id: string
  node: ReactNode
}

interface Props {
  /** Small pill contributions — overflow into a hamburger when the row runs out of width. */
  chips: ZoneChip[]
  /** Large full-width contribution(s) (e.g. the pinned prompt card). Always inline. */
  cards?: ReactNode
}

export function AboveInputZone({ chips, cards }: Props) {
  if (chips.length === 0 && !cards) return null
  return (
    <div className="wabove-zone">
      {chips.length > 0 && <OverflowChips chips={chips} />}
      {cards}
    </div>
  )
}

const GAP = 8        // px gap between chips (keep in sync with .wabove-chips gap)
const HAMBURGER = 40 // px reserved for the overflow trigger (button + gap) when it shows

function OverflowChips({ chips }: { chips: ZoneChip[] }) {
  const rowRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(chips.length)
  const [menuOpen, setMenuOpen] = useState(false)

  // Measure every chip at natural width (off-flow row) and fit as many leading chips as the
  // container allows; the rest go to the hamburger. Re-runs on container resize — width is
  // driven by the parent (full-width flex, nowrap), so toggling visibleCount can't feed back.
  useLayoutEffect(() => {
    const row = rowRef.current
    const measure = measureRef.current
    if (!row || !measure) return
    const recompute = () => {
      const avail = row.clientWidth
      const items = Array.from(measure.children) as HTMLElement[]
      const widths = items.map((el, i) => el.offsetWidth + (i > 0 ? GAP : 0))
      const total = widths.reduce((a, b) => a + b, 0)
      if (total <= avail) { setVisibleCount(items.length); return }
      const budget = avail - HAMBURGER
      let used = 0, count = 0
      for (let i = 0; i < widths.length; i++) {
        if (used + widths[i] <= budget) { used += widths[i]; count++ } else break
      }
      setVisibleCount(count)
    }
    recompute()
    const ro = new ResizeObserver(recompute)
    ro.observe(row)
    return () => ro.disconnect()
    // key on the chip-id signature, not the array ref (rebuilt every parent render): the
    // observer only needs to reset when the SET of chips changes, not on every stream frame.
  }, [chips.map((c) => c.id).join('|')]) // eslint-disable-line react-hooks/exhaustive-deps

  const overflow = visibleCount < chips.length
  const visible = chips.slice(0, visibleCount)
  const hidden = chips.slice(visibleCount)

  return (
    <div className="wabove-chips" ref={rowRef}>
      {/* off-flow measurement row — every chip at natural width, never visible */}
      <div className="wabove-measure" ref={measureRef} aria-hidden="true">
        {chips.map((c) => <div className="wabove-chip" key={c.id}>{c.node}</div>)}
      </div>
      {visible.map((c) => <div className="wabove-chip" key={c.id}>{c.node}</div>)}
      {overflow && (
        <div className="wabove-overflow">
          <button className="wabove-ham" type="button" aria-expanded={menuOpen}
            aria-label={`เพิ่มเติมอีก ${hidden.length} รายการ`} title="เพิ่มเติม"
            onClick={() => setMenuOpen((o) => !o)}>
            <Icon name="menu" size={16} />
            <span className="wabove-ham-count">{hidden.length}</span>
          </button>
          {menuOpen && (
            <>
              <div className="wabove-menu-backdrop" onClick={() => setMenuOpen(false)} />
              <div className="wabove-menu" role="menu">
                {hidden.map((c) => (
                  <div className="wabove-menu-item" key={c.id} onClick={() => setMenuOpen(false)}>
                    {c.node}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
